import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Zap, CheckCircle, XCircle, Clock, AlertCircle, RefreshCw } from 'lucide-react';
import { WizardData } from '../AIActivityWizard';
import { supabase } from '../../lib/supabase';
import { buildCurriculumContext, mapSubjectToSystemTerms } from '../../lib/curriculum-systems';

export interface BatchGenerationStepProps {
  wizardData: WizardData;
  onStepComplete: (data: Partial<WizardData>) => void;
  onNext: () => void;
  onPrevious: () => void;
  setIsGenerating: (generating: boolean) => void;
}

interface GeneratedActivity {
  id: string;
  title: string;
  subject: string;
  status: 'generating' | 'completed' | 'error';
  error?: string;
  content?: unknown;
}

const subjectNames: Record<string, string> = {
  math: 'Math',
  reading: 'Reading & Language',
  science: 'Science',
  social: 'Social Studies',
  focus: 'Focus & Attention'
};

export function BatchGenerationStep({ wizardData, onStepComplete, onNext, onPrevious, setIsGenerating }: BatchGenerationStepProps) {
  const [activities, setActivities] = useState<GeneratedActivity[]>([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const hasStartedRef = useRef(false);

  const currentChild = wizardData.createdChild || wizardData.selectedChild;
  const totalActivities = wizardData.activityPlan?.totalActivities || 0;

  const generateSingleActivity = useCallback(async (activity: GeneratedActivity, childId: string) => {
    try {
      // Build curriculum context if available
      let curriculumContext = '';
      if (wizardData.curriculumSelection) {
        try {
          curriculumContext = await buildCurriculumContext(
            wizardData.curriculumSelection,
            [activity.subject]
          );
        } catch (err) {
          console.warn('Failed to build curriculum context:', err);
        }
      }

      // Map subject to curriculum-appropriate terms
      const subjectTerms = wizardData.curriculumSelection 
        ? mapSubjectToSystemTerms(activity.subject, wizardData.curriculumSelection.countryCode)
        : [activity.subject];

      console.log(`Starting generation for activity: ${activity.id}`, {
        subject: activity.subject,
        childId,
        hasCustomPrompt: !!(wizardData.learningProfile?.customInstructions)
      });

      const { data, error } = await supabase.functions.invoke('generate-activity', {
        body: {
          childId: childId,
          subject: activity.subject,
          curriculumTargetId: wizardData.learningProfile?.curriculumTargets?.[0] || null,
          customPrompt: [
            wizardData.learningProfile?.customInstructions,
            curriculumContext,
            `Difficulty level: ${wizardData.activityPlan.difficulty}`,
            subjectTerms.length > 1 ? `Subject terms: ${subjectTerms.join(', ')}` : null
          ].filter(Boolean).join('\n\n'),
          learningObjectives: wizardData.learningProfile?.learningObjectives?.filter(obj => obj.trim()) || []
        }
      });

      if (error) {
        console.error(`Edge function error for activity ${activity.id}:`, error);
        throw error;
      }

      if (!data) {
        console.error(`No data returned for activity ${activity.id}`);
        throw new Error('No data returned from edge function');
      }

      if (!data.success) {
        console.error(`Generation failed for activity ${activity.id}:`, data.error);
        throw new Error(data.error || 'Generation failed');
      }

      console.log(`Successfully generated activity ${activity.id}:`, {
        activityId: data.activity?.id,
        title: data.activity?.title
      });

      updateActivityStatus(activity.id, 'completed', undefined, data);
    } catch (error: unknown) {
      console.error(`Error generating activity ${activity.id}:`, error);
      const errorMessage = error instanceof Error ? error.message : 'Generation failed';
      updateActivityStatus(activity.id, 'error', errorMessage);
    }
  }, [wizardData.curriculumSelection, wizardData.learningProfile, wizardData.activityPlan]);

  const updateActivityStatus = (activityId: string, status: 'completed' | 'error', error?: string, content?: unknown) => {
    setActivities(prev => prev.map(activity => 
      activity.id === activityId 
        ? { ...activity, status, error, content }
        : activity
    ));
  };

  const startBatchGenerationInternal = useCallback(async (activityList: GeneratedActivity[]) => {
    if (!currentChild) {
      console.error('No child available for activity generation');
      setActivities(prev => prev.map(a => ({
        ...a,
        status: 'error',
        error: 'No child available for activity generation'
      })));
      return;
    }

    const childId = currentChild.id;
    console.log('Starting activity generation for child:', childId);

    // Generate activities with staggered timing
    for (let i = 0; i < activityList.length; i++) {
      const activity = activityList[i];
      
      // Stagger the requests to avoid overwhelming the API
      setTimeout(async () => {
        try {
          await generateSingleActivity(activity, childId);
        } catch (error) {
          console.error(`Error generating activity ${activity.id}:`, error);
          updateActivityStatus(activity.id, 'error', 'Generation failed');
        }
      }, i * 2000); // 2 second delay between each request
    }
  }, [currentChild, generateSingleActivity]);

  // Initialize activities list
  useEffect(() => {
    if (wizardData.activityPlan?.subjects && !hasStartedRef.current && currentChild) {
      const activityList: GeneratedActivity[] = [];
      
      wizardData.activityPlan.subjects.forEach((subject) => {
        if (subject?.subject && subject?.count > 0) {
          for (let i = 0; i < subject.count; i++) {
            activityList.push({
              id: `${subject.subject}-${i + 1}`,
              title: `${subjectNames[subject.subject] || subject.subject} Activity ${i + 1}`,
              subject: subject.subject,
              status: 'generating'
            });
          }
        }
      });

      if (activityList.length > 0) {
        setActivities(activityList);
        
        // Start generation automatically
        if (!hasStartedRef.current) {
          hasStartedRef.current = true;
          setIsGenerating(true);
          setTimeout(() => {
            startBatchGenerationInternal(activityList);
          }, 1000);
        }
      }
    }
  }, [wizardData.activityPlan, setIsGenerating, currentChild, startBatchGenerationInternal]);

  // Update counters when activities change
  useEffect(() => {
    const completed = activities.filter(a => a.status === 'completed').length;
    const failed = activities.filter(a => a.status === 'error').length;
    
    // Only update if counts actually changed
    if (completed !== completedCount) {
      setCompletedCount(completed);
    }
    if (failed !== failedCount) {
      setFailedCount(failed);
    }

    // Update wizard data with results - only when activities actually change
    if (activities.length > 0 && (completed !== completedCount || failed !== failedCount)) {
      onStepComplete({
        generatedActivities: activities
      });
    }

    // Check if generation is complete
    if (activities.length > 0 && (completed + failed) === activities.length) {
      setIsGenerating(false);
    }
  }, [activities, setIsGenerating, completedCount, failedCount, onStepComplete]);


  const retryFailedActivities = () => {
    if (!currentChild) return;

    const failedActivities = activities.filter(a => a.status === 'error');
    
    // Reset failed activities to generating
    setActivities(prev => prev.map(activity => 
      activity.status === 'error' 
        ? { ...activity, status: 'generating', error: undefined }
        : activity
    ));

    setIsGenerating(true);

    // Retry failed activities
    failedActivities.forEach((activity, index) => {
      setTimeout(async () => {
        try {
          await generateSingleActivity(activity, currentChild.id);
        } catch (error) {
          console.error(`Error retrying activity ${activity.id}:`, error);
          updateActivityStatus(activity.id, 'error', 'Retry failed');
        }
      }, index * 2000);
    });
  };

  const getProgressPercentage = () => {
    if (activities.length === 0) return 0;
    return Math.round(((completedCount + failedCount) / activities.length) * 100);
  };

  const isComplete = activities.length > 0 && (completedCount + failedCount) === activities.length;

  // Early return checks after all hooks
  if (!currentChild) {
    return (
      <div className="text-center p-8">
        <div className="text-error-600 mb-4">Error: No child selected</div>
        <button onClick={onPrevious} className="px-4 py-2 bg-gray-600 text-white rounded-lg">
          Go Back
        </button>
      </div>
    );
  }
  
  if (!wizardData.activityPlan || !wizardData.activityPlan.subjects) {
    return (
      <div className="text-center p-8">
        <div className="text-error-600 mb-4">Error: No activity plan configured</div>
        <button onClick={onPrevious} className="px-4 py-2 bg-gray-600 text-white rounded-lg">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Generating Your Activities
        </h3>
        <p className="text-gray-600">
          Creating {totalActivities} personalized activities for {currentChild.name}
        </p>
      </div>

      {/* Progress Summary */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-gray-900 flex items-center">
            <Zap className="w-5 h-5 mr-2 text-blue-600" />
            Generation Progress
          </h4>
          <div className="text-right">
            <div className="text-2xl font-bold text-blue-600">{getProgressPercentage()}%</div>
            <div className="text-sm text-gray-600">
              {completedCount + failedCount} / {activities.length}
            </div>
          </div>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
          <div 
            className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-1000"
            style={{ width: `${getProgressPercentage()}%` }}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="text-xl font-bold text-green-600">{completedCount}</div>
            <div className="text-sm text-gray-600">Completed</div>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
            <div className="text-xl font-bold text-blue-600">
              {activities.filter(a => a.status === 'generating').length}
            </div>
            <div className="text-sm text-gray-600">In Progress</div>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <XCircle className="w-6 h-6 text-error-600" />
            </div>
            <div className="text-xl font-bold text-error-600">{failedCount}</div>
            <div className="text-sm text-gray-600">Failed</div>
          </div>
        </div>
      </div>

      {/* Activity List */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Activity Status</h4>
        
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {activities.map((activity) => (
            <div
              key={activity.id}
              className={`p-4 rounded-lg border transition-all ${
                activity.status === 'completed'
                  ? 'border-green-200 bg-green-50'
                  : activity.status === 'error'
                  ? 'border-red-200 bg-red-50'
                  : 'border-primary-200 bg-primary-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${
                    activity.status === 'completed'
                      ? 'bg-green-100'
                      : activity.status === 'error'
                      ? 'bg-red-100'
                      : 'bg-primary-100'
                  }`}>
                    {activity.status === 'completed' && <CheckCircle className="w-5 h-5 text-green-600" />}
                    {activity.status === 'error' && <XCircle className="w-5 h-5 text-error-600" />}
                    {activity.status === 'generating' && <Clock className="w-5 h-5 text-blue-600 animate-pulse" />}
                  </div>
                  
                  <div>
                    <h5 className="font-medium text-gray-900">{activity.title}</h5>
                    <p className="text-sm text-gray-600">{subjectNames[activity.subject]}</p>
                    {activity.error && (
                      <p className="text-sm text-error-600 mt-1">{activity.error}</p>
                    )}
                  </div>
                </div>

                <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                  activity.status === 'completed'
                    ? 'bg-green-100 text-green-700'
                    : activity.status === 'error'
                    ? 'bg-red-100 text-error-700'
                    : 'bg-primary-100 text-primary-700'
                }`}>
                  {activity.status === 'completed' && 'Ready'}
                  {activity.status === 'error' && 'Failed'}
                  {activity.status === 'generating' && 'Generating...'}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Retry Button for Failed Activities */}
      {failedCount > 0 && isComplete && (
        <div className="bg-orange-50 rounded-xl border border-orange-200 p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-6 h-6 text-orange-600 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                Some Activities Failed to Generate
              </h4>
              <p className="text-gray-600 mb-4">
                {failedCount} activities couldn't be generated. This can happen due to network issues or AI service availability.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={retryFailedActivities}
                  className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry Failed Activities
                </button>
                <button
                  onClick={onNext}
                  className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Continue Anyway
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {isComplete && failedCount === 0 && (
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            All Activities Generated Successfully!
          </h4>
          <p className="text-gray-600 mb-6">
            {completedCount} personalized activities are ready for {currentChild.name}.
            Click "Next" to see the summary and start learning!
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={onPrevious}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg font-medium transition-colors"
            >
              <span>Previous</span>
            </button>
            <button
              onClick={onNext}
              className="flex items-center space-x-2 px-6 py-2 bg-primary-600 text-white hover:bg-primary-700 rounded-lg font-medium transition-colors"
            >
              <span>Next</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}